/**
 * THE frozen constants module for the 17Q Discovery.
 *
 * The 51 word strings themselves live in ./questions.generated.ts, which is
 * produced mechanically from 17Q_Discovery_Build_Spec.md by
 * `npm run gen:questions`. They are never typed by hand. This module is the
 * accessor layer over them: lookups, the forced-choice value rule, and the
 * export-time guard.
 *
 * Everything downstream, the Worker, the Pages app, the export, and the tests,
 * reads the words from here and nowhere else.
 */

import {
  INSTRUMENT_VERSION,
  QS_TO_Q46,
  QUESTIONS,
  RESPONDENT_INSTRUCTION,
  SOURCE_WORKBOOK,
  SOURCE_WORKBOOK_SHA256,
} from './questions.generated.ts';
import { canonicalWordList, wordListChecksum } from './checksum.ts';
import type { AnswerValue, Question, Slot, ValueTriple } from './types.ts';

export {
  INSTRUMENT_VERSION,
  QS_TO_Q46,
  QUESTIONS,
  RESPONDENT_INSTRUCTION,
  SOURCE_WORKBOOK,
  SOURCE_WORKBOOK_SHA256,
};

/** Spec section 6. The instrument is 17 questions, not 16 and not 18. */
export const TOTAL_QUESTIONS = 17;

/** [1, 2, ... 17]. */
export const QS_POSITIONS: readonly number[] = QUESTIONS.map((q) => q.qsPosition);

/**
 * Where a Paste Blocks range lands in the workbook. Spec section 4.
 * Q1 is row 5, so a QS position maps to row `qsPosition + PASTE_ROW_OFFSET`.
 */
export const PASTE_TARGET_SHEET = 'Answer Here';
export const PASTE_ANCHOR_CELL = 'E5';
export const PASTE_RANGE = 'E5:F21';
export const PASTE_ROW_OFFSET = 4;

/**
 * Identifies the question set an export was built against. Spec section 9,
 * sheet 3. If this does not match the workbook the operator is holding, the
 * export is against a different question set and must not be pasted.
 */
export const WORD_LIST_CHECKSUM = wordListChecksum(
  canonicalWordList(INSTRUMENT_VERSION, QUESTIONS as readonly Question[]),
);

// ---------------------------------------------------------------------------
// Named errors. Spec section 9 requires the export to abort with a named error
// rather than emit a near-match, so these are types callers can branch on.
// ---------------------------------------------------------------------------

/**
 * A word string was not found in the frozen list for its question.
 * Thrown at export time. Never swallow this and never fall back to a fuzzy
 * match: emitting a near-match is the failure this whole design exists to
 * prevent. Spec section 4.
 */
export class FrozenWordListError extends Error {
  override readonly name = 'FrozenWordListError';
  readonly qsPosition: number;
  readonly field: string;
  readonly received: string;
  readonly expected: readonly string[];

  constructor(qsPosition: number, field: string, received: string, expected: readonly string[]) {
    super(
      `FrozenWordListError: Q${qsPosition} ${field} is ${JSON.stringify(received)}, ` +
        `which is not in the frozen word list for that question. ` +
        `Expected one of: ${expected.map((w) => JSON.stringify(w)).join(', ')}. ` +
        `Pasting this into the workbook would silently score 3/3/3 for this row.`,
    );
    this.qsPosition = qsPosition;
    this.field = field;
    this.received = received;
    this.expected = expected;
  }
}

/** MOST and LEAST were the same word. Spec FR3 and section 10. */
export class ForcedChoiceError extends Error {
  override readonly name = 'ForcedChoiceError';
  readonly qsPosition: number;
  readonly word: string;

  constructor(qsPosition: number, word: string) {
    super(
      `ForcedChoiceError: Q${qsPosition} has MOST and LEAST both set to ${JSON.stringify(word)}. ` +
        `They must be different words.`,
    );
    this.qsPosition = qsPosition;
    this.word = word;
  }
}

/** A qs_position outside 1..17. */
export class UnknownQuestionError extends Error {
  override readonly name = 'UnknownQuestionError';
  readonly qsPosition: number;

  constructor(qsPosition: number) {
    super(
      `UnknownQuestionError: no question at position ${qsPosition}. ` +
        `Valid positions are 1..${TOTAL_QUESTIONS}.`,
    );
    this.qsPosition = qsPosition;
  }
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

const BY_POSITION = new Map<number, Question>(QUESTIONS.map((q) => [q.qsPosition, q]));

export function getQuestion(qsPosition: number): Question {
  const q = BY_POSITION.get(qsPosition);
  if (!q) throw new UnknownQuestionError(qsPosition);
  return q;
}

export function isValidQsPosition(qsPosition: number): boolean {
  return BY_POSITION.has(qsPosition);
}

/**
 * The slot (1, 2 or 3) holding `word` in the given question, or null.
 *
 * Exact string comparison, deliberately. No trim, no case folding, no
 * normalisation. That is the same comparison the workbook performs, so a word
 * this function rejects is a word the workbook would score as 3.
 */
export function findSlot(qsPosition: number, word: string): Slot | null {
  const words = getQuestion(qsPosition).words;
  const index = words.indexOf(word);
  return index === -1 ? null : ((index + 1) as Slot);
}

/**
 * Returns the slot for `word`, or throws FrozenWordListError.
 * Use this on every word string on the way out of the system.
 */
export function assertFrozenWord(qsPosition: number, word: string, field: string): Slot {
  const slot = findSlot(qsPosition, word);
  if (slot === null) {
    throw new FrozenWordListError(qsPosition, field, word, getQuestion(qsPosition).words);
  }
  return slot;
}

// ---------------------------------------------------------------------------
// Forced-choice values. Spec FR6.
// ---------------------------------------------------------------------------

/**
 * MOST scores 5, LEAST scores 1, the third word scores 3.
 *
 * Computed at write time and stored, never derived at export. These values are
 * server-side only: they are never returned to the respondent-facing client.
 * Spec FR10.
 */
export function computeValues(qsPosition: number, mostSlot: Slot, leastSlot: Slot): ValueTriple {
  if (mostSlot === leastSlot) {
    // Slot is 1 | 2 | 3, so this index is always in range for a 3-word tuple.
    throw new ForcedChoiceError(qsPosition, getQuestion(qsPosition).words[mostSlot - 1]!);
  }
  const values: AnswerValue[] = [3, 3, 3];
  values[mostSlot - 1] = 5;
  values[leastSlot - 1] = 1;
  return values as unknown as ValueTriple;
}
